// Step 25 — Executive attention list.
//
// Aggregates the deterministic "requires attention" items across
// actions, contracts, preparedness observations, and unread
// notifications. Every row carries its actual backend source.
//
// Wording follows the neutral READINESS_LANGUAGE — we never claim
// "prevents closing" / "will not sign" / "at risk". Rows link into
// the existing human-controlled surfaces.

import { Link } from "react-router-dom";

import { AIEmpty, AISection } from "../AIPrimitives";
import { commandAttentionItems } from "./helpers";

const CATEGORY_LABEL = {
  action: "Åtgärd",
  contract: "Avtal",
  preparedness: "Beredskap",
  activity: "Aktivitet",
};

function _link(item, workspaceId) {
  if (item.category === "action" && item.action_id != null) {
    return {
      to: `/deals/${workspaceId}/actions`,
      label: `Öppna åtgärd #${item.action_id} →`,
    };
  }
  if (item.category === "contract" && item.contract_id != null) {
    return {
      to: `/deals/${workspaceId}/contracts/${item.contract_id}`,
      label: `Öppna avtal #${item.contract_id} →`,
    };
  }
  if (item.category === "activity" && item.action_id != null) {
    return {
      to: `/deals/${workspaceId}/actions`,
      label: `Öppna åtgärd #${item.action_id} →`,
    };
  }
  if (item.category === "activity") {
    return {
      to: `/deals/${workspaceId}/activity`,
      label: "Öppna aktivitet →",
    };
  }
  return null;
}

export default function AICommandExecutiveAttention({
  actions,
  contracts,
  situation,
  notifications,
  workspaceId,
  now,
}) {
  const items = commandAttentionItems({
    actions, contracts, situation, notifications, now,
  });
  if (items.length === 0) {
    return (
      <AISection title="Kräver uppmärksamhet">
        <AIEmpty
          title="Inga öppna poster kräver uppmärksamhet"
          hint={
            "Poster dyker upp här när DealActions blockeras eller går " +
            "över förfallodatum, avtal väntar granskning, beredskapsgap " +
            "registreras, eller olästa notiser kommer in."
          }
        />
      </AISection>
    );
  }
  return (
    <AISection
      title="Kräver uppmärksamhet"
      right={
        <span className="qai-page-section-hint">
          {items.length} post{items.length === 1 ? "" : "er"} · deterministisk data
        </span>
      }
    >
      <ul className="qai-command-attention">
        {items.map((it) => {
          const link = _link(it, workspaceId);
          return (
            <li
              key={it.key}
              className="qai-command-attention-row"
              data-severity={it.severity}
              data-category={it.category}
            >
              <div className="qai-command-attention-main">
                <div className="qai-command-attention-title">{it.title}</div>
                <div className="qai-command-attention-meta">
                  <span className="qai-command-attention-category">
                    {CATEGORY_LABEL[it.category] || it.category}
                  </span>
                  <span> · </span>
                  <span className="qai-command-attention-reason">
                    {it.reason}
                  </span>
                  {it.source?.source_id && (
                    <>
                      <span> · </span>
                      <span className="qai-command-attention-source">
                        {it.source.source_type} #{it.source.source_id}
                      </span>
                    </>
                  )}
                </div>
              </div>
              {link && (
                <Link to={link.to} className="qai-page-section-link">
                  {link.label}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </AISection>
  );
}
